import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActionButton } from './MediaRecorderComponent.styles';
import { ReactComponent as MicrophoneIcon } from '../../assets/icons/microphone.svg';
import { ReactComponent as StopIcon } from '../../assets/icons/stop_circle.svg';
import { ReactComponent as TrashIcon } from '../../assets/icons/trash.svg';
import { Text15Regular } from '../utils/Text/Text.styles';
import { notifyError } from '../../utils/notify';

const MediaRecorderComponent = ({ recordedVoice, setRecordedVoice }) => {
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [recordingTime, setRecordingTime] = useState(`00:00,00`);

  const interval = useRef(null);

  const formatTime = time => {
    const milliseconds = parseInt((time % 1000) / 10, 10);
    const seconds = parseInt((time / 1000) % 60, 10);
    const minutes = parseInt((time / (1000 * 60)) % 60, 10);

    const formattedMinutes = minutes < 10 ? '0' + minutes : minutes;
    const formattedSeconds = seconds < 10 ? '0' + seconds : seconds;
    const formattedMilliseconds = milliseconds < 10 ? '0' + milliseconds : milliseconds;

    return `${formattedMinutes}:${formattedSeconds},${formattedMilliseconds}`;
  };

  const updateTimeElapsed = useCallback(() => {
    if (startTime) {
      const now = Date.now();
      const newTimeElapsed = now - startTime;
      setRecordingTime(formatTime(newTimeElapsed));
    }
  }, [startTime]);

  const isRecording = useMemo(() => mediaRecorder?.state === 'recording', [mediaRecorder?.state]);

  const requestMicrophonePermission = () => {
    if (navigator.mediaDevices) {
      navigator.mediaDevices
        .getUserMedia({ audio: true, video: false })
        .then(stream => {
          const recorder = new MediaRecorder(stream);
          recorder.ondataavailable = async event => {
            const audioBlob = event.data;
            const audioUrl = URL.createObjectURL(audioBlob);
            const arrayBuffer = await audioBlob.arrayBuffer();
            const audioContext = new AudioContext();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
            const duration = audioBuffer.duration;
            setRecordedVoice({ file: audioBlob, url: audioUrl, duration });
          };

          setMediaRecorder(recorder);
          setStartTime(Date.now());
          recorder.start();
        })
        .catch(error => {
          if (error.message === 'Permission denied') {
            notifyError('Please enable microphone access!');
          }
        });
    }
  };

  useEffect(() => {
    if (startTime) {
      interval.current = setInterval(updateTimeElapsed, 10);
    }
    return () => clearInterval(interval.current);
  }, [startTime, updateTimeElapsed]);

  const startRecording = e => {
    e.preventDefault();
    if (mediaRecorder && mediaRecorder.state === 'inactive') {
      setStartTime(Date.now());
      mediaRecorder.start();
    } else {
      requestMicrophonePermission();
    }
  };

  const stopRecording = e => {
    e.preventDefault();
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      clearInterval(interval.current);

      mediaRecorder.stop();
    }
  };

  const deleteRecordedVoice = e => {
    e.preventDefault();
    setStartTime(null);
    setRecordedVoice(null);
  };

  if (!navigator.mediaDevices) {
    return null;
  }

  return (
    <ActionButton
      onClick={e => (isRecording ? stopRecording(e) : recordedVoice ? deleteRecordedVoice(e) : startRecording(e))}>
      {isRecording ? (
        <>
          <StopIcon fill="#5f2ccb" />
          <Text15Regular>{recordingTime}</Text15Regular>
        </>
      ) : recordedVoice ? (
        <TrashIcon color="#5f2ccb" />
      ) : (
        <>
          <MicrophoneIcon color="#5f2ccb" />
          <Text15Regular>Record Audio</Text15Regular>
        </>
      )}
    </ActionButton>
  );
};

export default MediaRecorderComponent;
